// ─────────────────────────────────────────────
// Oike — the main navigation by areas of the company (map approved by Ale on 03-11-2026; docs/diseno.md):
// Hoy, Gestión de clientes, Operaciones (only her internal work), Marketing, Comercial and Finanzas, with
// Configuración at the foot of the menu. No directory or global view appears twice: the contacts, companies and
// participants are in one Directorio, and the processes in Acompañamientos. Every entry opens a screen that already exists: moving an entry never moves data nor changes who
// may open it, and a pending function never gets an empty entry. Shared by App.jsx and the browser demo.
// - "Hoy" is the first entry and the home page: for the owner, her private day; for everyone else, their commercial
//   follow-up.
// - Mi espacio's parts are one screen (OwnerSpace) opened on the part of each entry (OWNER_PAGES); Acompañamiento's
//   parts are one screen (CoachingHub, COACHING_PAGES); Actividad e informes gathers existing commercial screens
//   (REPORT_PAGES). Equipo is reached from Configuración.
// - Old page keys and links keep working: no key was renamed (followup and library open the owner's own entry).
// Who can open each section is decided by the server; the menu only reflects it.
// ─────────────────────────────────────────────

export const GROUPS = [
  { key: 'today', label: 'Hoy' },
  { key: 'clients', label: 'Gestión de clientes' },
  { key: 'operations', label: 'Operaciones' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'sales', label: 'Comercial' },
  { key: 'finance', label: 'Finanzas' },
];
// Subheadings inside an area (none at the moment: Operaciones holds only the internal work)
export const SUBGROUPS = {};
export const GROUP_LABEL = Object.fromEntries(GROUPS.map(g => [g.key, g.label]));

// area: the permission area (Negocio, Acompañamiento, Privado), shown as a small tag
// admin: Oike administration of the central base · owner: only the owner (Mi espacio) · notOwner: everyone but her
// bdr: also the BDR role
const SECTIONS = [
  { key: 'followup', label: 'Hoy', group: 'today', area: 'business', bdr: true, notOwner: true },
  { key: 'myspace', label: 'Hoy', group: 'today', area: 'mine', owner: true },
  // One directory of people and companies (contacts, participants and companies; never two lists of the same people)
  { key: 'directory', label: 'Directorio', group: 'clients', area: 'business', bdr: true },
  { key: 'diagnostics', label: 'Diagnósticos', group: 'clients', area: 'business', admin: true },
  // Individual and group processes; their programs are inside (no other entry called «Procesos» or «Programas»)
  { key: 'coaching', label: 'Acompañamientos', group: 'clients', area: 'coaching', admin: true },
  { key: 'courses', label: 'Cursos', group: 'clients', area: 'coaching', admin: true },
  { key: 'agenda', label: 'Agenda y sesiones', group: 'clients', area: 'coaching', admin: true },
  // Activities still to do (processes) apart from submitted work to review (courses): never the same state
  { key: 'deliveries', label: 'Actividades y entregas', group: 'clients', area: 'coaching', admin: true },
  { key: 'coachmessages', label: 'Mensajes', group: 'clients', area: 'coaching', admin: true },
  { key: 'planning', label: 'Planificación y tareas', group: 'operations', area: 'mine', owner: true },
  { key: 'goals', label: 'Objetivos y revisión', group: 'operations', area: 'mine', owner: true },
  { key: 'mytime', label: 'Mi tiempo', group: 'operations', area: 'mine', owner: true },
  // The owner's one library (materials, notes, sources and tools of the method); other administrators keep the method's
  { key: 'mylibrary', label: 'Mi biblioteca', group: 'operations', area: 'mine', owner: true },
  { key: 'library', label: 'Biblioteca del método', group: 'operations', area: 'coaching', admin: true, notOwner: true },
  { key: 'brand', label: 'Mi marca', group: 'marketing', area: 'mine', owner: true },
  { key: 'contentlab', label: 'Contenido', group: 'marketing', area: 'business', admin: true },
  { key: 'campaigns', label: 'Campañas', group: 'marketing', area: 'business', admin: true },
  { key: 'landings', label: 'Landings', group: 'marketing', area: 'business', admin: true },
  { key: 'events', label: 'Eventos y webinars', group: 'marketing', area: 'business' },
  { key: 'icp', label: 'Perfil de cliente ideal', group: 'marketing', area: 'business', admin: true },
  // Showing Message Lab never authorizes sending, sequences or automations: its own rules and the server decide
  { key: 'messagelab', label: 'Prospección y mensajes', group: 'sales', area: 'business', admin: true },
  { key: 'requests', label: 'Solicitudes', group: 'sales', area: 'business' },
  // The same follow-up queue the owner has under Hoy (same records), as an entry of its own
  { key: 'followups', label: 'Seguimientos', group: 'sales', area: 'business', owner: true },
  { key: 'proposals', label: 'Pipeline', group: 'sales', area: 'business' },
  { key: 'quotes', label: 'Cotizaciones', group: 'sales', area: 'business' },
  { key: 'activity', label: 'Actividad e informes', group: 'sales', area: 'business' },
  // Movimientos: what was registered in a period (income and payments, pending, to review), only the owner
  { key: 'movements', label: 'Movimientos', group: 'finance', area: 'mine', owner: true },
  // Resultados (B1): sales, collected, ticket and expenses registered by date, only the owner
  { key: 'results', label: 'Resultados', group: 'finance', area: 'mine', owner: true },
  { key: 'income', label: 'Ingresos', group: 'finance', area: 'mine', owner: true },
  // Gastos: business and personal expenses and their payments, only the owner (expenses.mts checks it)
  { key: 'expenses', label: 'Gastos', group: 'finance', area: 'mine', owner: true },
  { key: 'collections', label: 'Cobros y saldos', group: 'finance', area: 'business', admin: true },
];

export const AREAS = { business: 'Negocio', coaching: 'Acompañamiento', mine: 'Privado' };

const isOwner = ({ isOikeAdmin, ownerSpace }) => !!(isOikeAdmin && ownerSpace);
const allowed = (s, opts) => {
  const owner = isOwner(opts);
  return (!s.admin || opts.isOikeAdmin) && (!s.owner || owner) && (!s.notOwner || !owner) && (!opts.isBDR || s.bdr);
};

/** The menu entries one login may open: BDRs their part of the business; coach sections and Mi espacio only when allowed. */
export const navItemsFor = (opts) => SECTIONS.filter(s => allowed(s, opts));

/** The areas with at least one entry for this login, each with its entries in order (an area without entries is not shown). */
export function navGroupsFor(opts) {
  const items = navItemsFor(opts);
  return GROUPS.map(g => ({ ...g, items: items.filter(i => i.group === g.key) })).filter(g => g.items.length);
}

// ── Mi espacio: one screen, opened on the part of each entry ──
export const OWNER_PAGES = { myspace: 'day', planning: 'planning', mytime: 'time', goals: 'review', mylibrary: 'library', ask: 'ask', income: 'finance', brand: 'brand' };
export const isOwnerPage = (page) => Object.prototype.hasOwnProperty.call(OWNER_PAGES, page);
export const ownerTabOf = (page) => OWNER_PAGES[page] || 'day';
export const pageOfOwnerTab = (tab) => Object.keys(OWNER_PAGES).find(k => OWNER_PAGES[k] === tab) || 'myspace';

// ── Acompañamiento: one screen, opened on the part of each entry ──
// people: a participant's card (opened from the Directorio) and the card management; programs: inside Acompañamientos
export const COACHING_PAGES = { coaching: 'processes', people: 'people', programs: 'programs', courses: 'learning', agenda: 'agenda', deliveries: 'work', coachmessages: 'messages' };
export const isCoachingPage = (page) => Object.prototype.hasOwnProperty.call(COACHING_PAGES, page);
export const coachingTabOf = (page) => COACHING_PAGES[page] || 'processes';
export const pageOfCoachingTab = (tab) => Object.keys(COACHING_PAGES).find(k => COACHING_PAGES[k] === tab) || 'coaching';

// ── Actividad e informes: existing commercial screens together (old keys overview and solutionshub open their tab) ──
export const REPORT_PAGES = { activity: 'activity', overview: 'overview', solutionshub: 'prices' };
export const isReportPage = (page) => Object.prototype.hasOwnProperty.call(REPORT_PAGES, page);
export const reportTabOf = (page) => REPORT_PAGES[page] || 'activity';
export const pageOfReportTab = (tab) => Object.keys(REPORT_PAGES).find(k => REPORT_PAGES[k] === tab) || 'activity';

/** A page shown under another entry of the menu. */
// contacts (contact management), client (a person's unified file), accounts (companies and their file) and people
// (participant cards management) are the Directorio's; programs is inside Acompañamientos
const ACTIVE_ALIAS = { ask: 'mylibrary', overview: 'activity', solutionshub: 'activity', contacts: 'directory', accounts: 'directory', people: 'directory', client: 'directory', programs: 'coaching' };
export const activeKeyOf = (page) => ACTIVE_ALIAS[page] || page;

/** The page to show for a saved or linked one: the owner's old "Mi día" opens her Hoy and the method library her own. */
export function resolvePage(page, opts) {
  if (isOwner(opts) && page === 'followup') return 'myspace';
  if (isOwner(opts) && page === 'library') return 'mylibrary';
  return page;
}

/**
 * Every page one login can reach from the menu: its entries, the pages shown under one of them (Consultar, the
 * reports) and Equipo from Configuración (administration). Used by the tests: no login loses a page it had.
 */
export function reachablePagesFor(opts) {
  const keys = new Set(navItemsFor(opts).map(i => i.key));
  for (const [page, target] of Object.entries(ACTIVE_ALIAS)) if (keys.has(target)) keys.add(page);
  // A participant's card and the card management: only for the coach's audience (the administration), never via the directory alias alone
  if (!opts.isOikeAdmin) keys.delete('people');
  if (opts.isOikeAdmin) keys.add('team');
  if (isOwner(opts)) { keys.add('library'); keys.add('followup'); }
  return keys;
}

/** «Área · Entrada» for the top bar (a single-entry area shows its own name). */
export function pageLabelOf(page, opts) {
  if (page === 'team' && opts.isOikeAdmin) return 'Configuración · Equipo';
  const item = navItemsFor(opts).find(i => i.key === activeKeyOf(page));
  if (!item) return 'Oike';
  const group = GROUP_LABEL[item.group];
  return item.label === group ? item.label : `${group} · ${item.label}`;
}
